import {
  getLanguageString,
  getCbtTestTypeLabel,
  isCbtProgramme
} from '@cont/SeeAll/utils';
import { useCardsInfo } from '@cont/Store/TestSeries/FullSchedule/CardsContainer';
import ScheduleCard from '@cont/Store/TestSeries/FullSchedule/ScheduleCard';
import styled from '@emotion/styled';
import { useIsMobile } from '@stores/AppCommonData';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Dynamic from '@base/Dynamic/Dynamic';
import Image from '@base/Image';
import { CBT_TEST_TYPE_ICON_SRC } from '@cont/SeeAll/constants';
import { getDateMonthString } from '@utils/dates';
import { pluralize, textTruncate } from '@utils/textHelper';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';
import React, { forwardRef, useState } from 'react';
import LiveClasses from 'aquilla/icons/unacademyStore/LiveClasses';
import Language from 'aquilla/icons/unacademyStore/Language';
import useTestSeriesInfo from './useTestSeriesInfo';
import useTestSeriesEvents from './useTestSeriesEvents';

const Modals = Dynamic(() =>
  import('@cont/Store/TestSeries/FullSchedule/Modals')
);

const FeaturedWrapper = styled.div`
  margin-bottom: ${Spacings.SPACING_14B};

  & .h4_variant {
    margin-bottom: ${Spacings.SPACING_6B};
  }
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_4B};
    & .h4_variant {
      margin-bottom: ${Spacings.SPACING_3B};
    }
  }
`;
const Title = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_6B};
`;

const StyledButton = styled(Button)`
  width: 225px;
  height: ${Spacings.SPACING_12B};
  margin-top: ${Spacings.SPACING_6B};

  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const StyledP1 = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
`;

const Languages = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_6B};
`;

const LanguageDetails = styled.div`
  margin-left: ${Spacings.SPACING_3B};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 212px;
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Count = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
`;

const ImageWrapper = styled.div`
  margin-right: ${Spacings.SPACING_3B};
`;

const ReadMore = styled.span`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.P1};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
  margin: ${Spacings.SPACING_0B};
  transition: all 300ms ease;
  margin-top: ${Spacings.SPACING_8B};
  color: var(--color-text-primary);
  cursor: pointer;
`;

const DescriptionWrapper = styled.div`
  margin-top: ${Spacings.SPACING_6B};
`;

const Description = styled.span`
  white-space: pre-line;
  font-size: ${Fonts.P1};
  line-height: 20px;
  color: var(--color-text-primary);
`;

const CountWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 160px;
`;

const TEXT_LIMIT = 456;
const MOBILE_TEXT_LIMIT = 180;

const About = forwardRef(({ isPurchaseActive }, ref) => {
  const { data } = useTestSeriesInfo();
  const [isTextExpanded, setIsTextExpanded] = useState(false);

  const {
    classCount,
    quizCount,
    availableLanguages: languages,
    description,
    brochure,
    featuredItem = {},
    programmeType,
    programmeComposition
  } = data;

  const { sendViewBrochureEvent } = useTestSeriesEvents(isPurchaseActive);

  const isMobile = useIsMobile();

  const getLanguageHeading = () => {
    return `Test ${pluralize(languages.length, 'Language', 's', true)}`;
  };
  const renderLanguages = () => {
    if (!languages || !languages.length) {
      return null;
    }
    return (
      <Languages>
        <Language width={40} height={40} />
        <LanguageDetails>
          <Typography variant="p3" color="var(--color-text-secondary)">
            {getLanguageHeading()}
          </Typography>
          <StyledP1>{getLanguageString(languages)}</StyledP1>
        </LanguageDetails>
      </Languages>
    );
  };

  const renderTestType = () => {
    if (!isCbtProgramme(programmeType) || programmeComposition == null) {
      return null;
    }

    return (
      <Languages>
        <Image
          src={CBT_TEST_TYPE_ICON_SRC}
          width={40}
          height={40}
          alt="Test type"
        />
        <LanguageDetails>
          <Typography variant="p3" color="var(--color-text-secondary)">
            Test type
          </Typography>
          <StyledP1>{getCbtTestTypeLabel(programmeComposition)}</StyledP1>
        </LanguageDetails>
      </Languages>
    );
  };

  const handleDownload = () => {
    window.open(brochure);
    sendViewBrochureEvent();
  };

  const processedFeatureCard = {
    cards: [
      {
        ...featuredItem,
        isFeatured: true
      }
    ],
    date: getDateMonthString(featuredItem?.value?.startTime, false, false)
  };

  const { showSaveIcon, hasSkuAccess, isSubscribed, collectionUid } =
    useCardsInfo(processedFeatureCard.cards[0]);

  const textLimit = isMobile ? MOBILE_TEXT_LIMIT : TEXT_LIMIT;
  return (
    <div ref={ref}>
      {featuredItem && (
        <FeaturedWrapper>
          <ScheduleCard
            showSaveIcon={showSaveIcon}
            hasSkuAccess={hasSkuAccess}
            isSubscribed={isSubscribed}
            collectionUid={collectionUid}
            isCbt={isCbtProgramme(programmeType)}
            cardsInfo={processedFeatureCard}
          />
          <Modals />
        </FeaturedWrapper>
      )}

      {!isMobile && <Title variant="h4">About</Title>}
      {(quizCount > 0 || classCount > 0) && (
        <FlexRow>
          <ImageWrapper>
            <LiveClasses width={40} height={40} />
          </ImageWrapper>

          <CountWrapper>
            {quizCount > 0 && (
              <FlexColumn>
                <Typography variant="p3" color="var(--color-text-secondary)">
                  Mock tests
                </Typography>
                <Count variant="p1">{quizCount}</Count>
              </FlexColumn>
            )}
            {classCount > 0 && (
              <FlexColumn>
                <Typography variant="p3" color="var(--color-text-secondary)">
                  Doubt classes
                </Typography>
                <Count variant="p1">{classCount}</Count>
              </FlexColumn>
            )}
          </CountWrapper>
        </FlexRow>
      )}
      {renderLanguages()}
      {renderTestType()}
      <DescriptionWrapper
        onClick={() => {
          setIsTextExpanded((bool) => !bool);
        }}
      >
        {isTextExpanded ? (
          <Description>{description}</Description>
        ) : (
          <>
            <Description>{textTruncate(description, textLimit)}</Description>
            {description?.length > textLimit && <ReadMore>Read more</ReadMore>}
          </>
        )}
      </DescriptionWrapper>

      {brochure && (
        <StyledButton
          label="View brochure"
          variant="secondary"
          size="large"
          onClick={handleDownload}
        />
      )}
    </div>
  );
});

About.displayName = 'About';
export default React.memo(About);
